import { Text, View, ScrollView, Share, Pressable, RefreshControl } from 'react-native';
import { useUnistyles, StyleSheet } from 'react-native-unistyles';
import { useEffect, useState, useRef, useCallback } from 'react';
import { getMonthlyEventInstances } from '@/data/events';
import { deleteCalendar } from '@/data/calendars';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Calendar from '@/components/Events/Calendar';
import AddOccurrencePicker from '@/components/Events/AddOccurrencePicker';
import LocationScheduleModal from '@/components/Events/LocationScheduleModal';
import { LegendList } from "@legendapp/list";
import EventInstanceCard from '@/components/Events/EventInstanceCard';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Holidays from 'date-holidays';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import BlackoutPeriodItem from '@/components/Events/BlackoutPeriodItem';
import * as Linking from 'expo-linking';
import SearchBox from '@/components/General/inputs/SearchBox';
import usePlatform from '@/utils/hooks/usePlatform';
import SheetModal from '@/components/General/SheetModal';
import { ActivityIndicator } from 'react-native-paper';
import { showAlert } from '@/utils/showAlert';
import { useNavigation, useRouter } from 'expo-router';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import Unicon from '@/components/General/Unicon';
import SolidButton from '@/components/General/SolidButton';
import useGuide from '@/utils/hooks/useGuide';
import GuideTarget from '@/components/Guides/GuideTarget';

const CalendarScreen = () => {
    const [dayInstances, setDayInstances] = useState([]);
    const [, setBlackoutDatesMap] = useState({});
    const [blackoutCountsMap, setBlackoutCountsMap] = useState({});
    const [markedDates, setMarkedDates] = useState({});
    const [eventsByDate, setEventsByDate] = useState({});
    const [holidays, setHolidays] = useState([]);
    const [filteredCalendars, setFilteredCalendars] = useState([]);

    const [calendarSheetOpen, setCalendarSheetOpen] = useState(false);
    const [blackoutSheetOpen, setBlackoutSheetOpen] = useState(false);
    const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
    const [addOccurrenceDate, setAddOccurrenceDate] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [monthShown, setMonthShown] = useState(new Date().getMonth());
    const [yearShown, setYearShown] = useState(new Date().getFullYear());
    const [searchText, setSearchText] = useState("");
    const [eventSearchText, setEventSearchText] = useState("");
    const [selectedLocations, setSelectedLocations] = useState([]);

    const [calendarWrapHeight, setCalendarWrapHeight] = useState(0);

    // Portrait: the day-events card is split between the list header (calendar +
    // card top) and the virtualized rows, so the card's minHeight is enforced by
    // a spacer footer measured from the rendered content.
    const [portraitHeaderHeight, setPortraitHeaderHeight] = useState(0);
    const [portraitCardTopHeight, setPortraitCardTopHeight] = useState(0);
    const [portraitCardSpacer, setPortraitCardSpacer] = useState(0);

    const { theme: { colors, fonts } } = useUnistyles();
    const { isIos, isLandscape, isPortrait, isWeb } = usePlatform();

    // On web/landscape, constrain calendar day-box height so the calendar
    // matches the right panel height instead of using the full window height.
    const calendarDayBoxHeight = isLandscape && calendarWrapHeight > 0
        ? Math.max(60, (calendarWrapHeight - 67) / 6)
        : undefined;

    const guideScrollRef = useRef(null);
    // GuideTarget's ensureVisible expects a ScrollView-shaped ref (getInnerViewRef
    // + scrollTo). Portrait hands guideScrollRef to LegendList instead of
    // ScrollView — adapt through getNativeScrollRef() so guide scrolling works
    // against whichever scroller is mounted.
    const guideScrollAdapter = useRef({
        getInnerViewRef: () => {
            const scroller = guideScrollRef.current;
            return scroller?.getInnerViewRef?.() || scroller?.getNativeScrollRef?.()?.getInnerViewRef?.();
        },
        scrollTo: (opts) => {
            const scroller = guideScrollRef.current;
            const target = scroller?.scrollTo ? scroller : scroller?.getNativeScrollRef?.();
            target?.scrollTo?.(opts);
        },
    });
    const queryClient = useQueryClient();
    const navigation = useNavigation();
    const router = useRouter();

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { data: blackoutDates } = Query.blackoutDates.byMonth({ month: monthShown, year: yearShown });
    const { data: monthEventInstances, isFetching: eventsAreLoading } = useQuery({
        queryKey: ['eventInstances', monthShown, yearShown],
        queryFn: () => getMonthlyEventInstances(monthShown, yearShown),
        staleTime: Infinity
    });
    const { data: allCalendars } = Query.calendars.all();
    const { data: allLocations } = Query.locations.all();
    const guide = useGuide('events.calendar', { ready: !eventsAreLoading && !!allCalendars && !!allLocations });

    const updateEventListRef = useRef();
    updateEventListRef.current = eventsAreLoading ? [] : monthEventInstances?.filter(i => {
        // Filter by calendar
        if (filteredCalendars.includes(i.Event?.calendarID)) return false;
        
        // Filter by location (check EventInstance.Location first, then Event.Location)
        if (selectedLocations.length > 0) {
            const instanceLocationId = i.Location?.id || i.locationID;
            const eventLocationId = i.Event?.Location?.id || i.Event?.locationID;
            const locationId = instanceLocationId || eventLocationId;
            if (!locationId || !selectedLocations.includes(locationId)) return false;
        }
        
        // Filter by search text (check Event.name and EventInstance.nameOverride)
        if (eventSearchText.trim()) {
            const searchLower = eventSearchText.toLowerCase().trim();
            const eventName = i.Event?.name?.toLowerCase() || '';
            const nameOverride = i.nameOverride?.toLowerCase() || '';
            if (!eventName.includes(searchLower) && !nameOverride.includes(searchLower)) return false;
        }
        
        return true;
    });

    const deleteCalendarMutation = useMutation({
        mutationFn: deleteCalendar,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.calendars.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.events.all() });
        },
    });

    useEffect(() => {
        getFilteredCalendars();
    }, []);

    useEffect(() => {
        var hd = new Holidays('US');
        let holidays = hd.getHolidays(yearShown);
        setHolidays(holidays);
    }, [yearShown]);

    useEffect(() => {
        navigation.setOptions({
            headerShown: true,
            headerRight: () => (
                <GuideTarget id="events.actions" style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    {isLandscape ? (
                        // Desktop: Show all buttons directly
                        (<>
                            {loggedInUser?.can?.("Create Events") && (
                                <SolidButton 
                                    testID="new-event-button"
                                    size="sm" 
                                    iconName="add" 
                                    iconBefore 
                                    text="New Event" 
                                    onPress={() => router.push({ pathname: "/events/create", params: { defaultStartTime: selectedDate.toISOString() } })}
                                />
                            )}
                            <SolidButton 
                                testID="calendars-button"
                                size="sm" 
                                type="textSecondary" 
                                variant="outlined" 
                                iconName="calendar-outline" 
                                iconBefore 
                                text="Calendars" 
                                onPress={() => setCalendarSheetOpen(true)} 
                            />
                            <SolidButton 
                                testID="locations-button"
                                size="sm" 
                                type="textSecondary" 
                                variant="outlined" 
                                iconName="location-outline" 
                                iconBefore 
                                text="Locations" 
                                onPress={() => setScheduleModalOpen(true)} 
                            />
                            <SolidButton
                                size="sm"
                                type="textSecondary"
                                variant="outlined"
                                iconName="ban-outline"
                                iconBefore
                                text="Add Blackout"
                                onPress={() => router.push({ pathname: "/events/blackouts/create" })}
                            />
                            <SolidButton
                                testID="service-flow-templates"
                                size="sm"
                                type="textSecondary"
                                variant="outlined"
                                iconName="albums-outline"
                                iconBefore
                                text="Service Flow templates"
                                onPress={() => router.push({ pathname: "/events/service-flow" })}
                            />
                        </>)
                    ) : (
                        // Mobile: Just show dropdown
                        (<DropdownMenu
                            options={[
                                ...(loggedInUser?.can?.("Create Events") ? [{ name: "New Event Series", onSelect: () => router.push({ pathname: "/events/create", params: { defaultStartTime: selectedDate.toISOString() } }) }] : []),
                                { name: "Manage Calendars", onSelect: () => setCalendarSheetOpen(true) },
                                { name: "Room Schedule", onSelect: () => setScheduleModalOpen(true) },
                                { name: "Add Blackout", onSelect: () => router.push({ pathname: "/events/blackouts/create" }) },
                                { name: "Service Flow templates", onSelect: () => router.push({ pathname: "/events/service-flow" }) },
                                ...(guide ? [{ name: "Tour this page", icon: dropdownIcons.help, onSelect: () => guide.replay('events.calendar') }] : []),
                            ]}
                        />)
                    )}
                </GuideTarget>
            )
        });
    }, [navigation, loggedInUser, selectedDate, isPortrait, isLandscape, colors, fonts, guide]);

    useEffect(() => {
        if (!eventsAreLoading) {
            let colorsWithDates = {};
            let eventsWithDates = {};

            updateEventListRef.current?.forEach(i => {
                if (filteredCalendars.includes(i.Event?.calendarID)) {
                    return;
                }

                const startDate = new Date(i.startTime);
                const startDateString = startDate.toDateString();
                const endDate = i.endTime ? new Date(i.endTime) : null;
                const endDatestring = i.endTime ? endDate.toDateString() : null;

                const eventData = {
                    id: i.id,
                    name: i.nameOverride || i.Event?.name,
                    startTime: i.startTime,
                    allDay: i.allDay === true,
                    color: i.Event?.Calendar?.color
                };

                if (endDatestring && startDateString != endDatestring) {
                    endDate.setHours(0, 0, 0, 0);
                    startDate.setHours(0, 0, 0, 0);
                    const days = ((((endDate?.getTime() - startDate.getTime()) / 1000) / 60) / 60) / 24;

                    for (let j = 1; j <= days; j++) {
                        const anotherDay = new Date(startDate);
                        anotherDay.setDate(startDate.getDate() + j);
                        const anotherDateString = anotherDay.toDateString();

                        if (!colorsWithDates[anotherDateString]) {
                            colorsWithDates[anotherDateString] = [];
                        }
                        if (!colorsWithDates[anotherDateString].includes(i.Event?.Calendar?.color)) {
                            colorsWithDates[anotherDateString].push(i.Event?.Calendar?.color);
                        }
                        if (!eventsWithDates[anotherDateString]) {
                            eventsWithDates[anotherDateString] = [];
                        }
                        eventsWithDates[anotherDateString].push({ ...eventData, startTime: anotherDay.toISOString() });
                    }
                }

                if (!colorsWithDates[startDateString]) {
                    colorsWithDates[startDateString] = [];
                }
                if (!colorsWithDates[startDateString].includes(i.Event?.Calendar?.color)) {
                    colorsWithDates[startDateString].push(i.Event?.Calendar?.color);
                }
                if (!eventsWithDates[startDateString]) {
                    eventsWithDates[startDateString] = [];
                }
                eventsWithDates[startDateString].push(eventData);
            });

            holidays.forEach(holiday => {
                const theDate = new Date(holiday.date);
                if (theDate.getMonth() == monthShown) {
                    const dateString = theDate.toDateString();
                    if (!colorsWithDates[dateString]) {
                        colorsWithDates[dateString] = [];
                    }
                    if (!colorsWithDates[dateString].includes(colors.text)) {
                        colorsWithDates[dateString].push(colors.text);
                    }
                    if (!eventsWithDates[dateString]) {
                        eventsWithDates[dateString] = [];
                    }
                    eventsWithDates[dateString].push({
                        id: 'holiday_' + holiday.name,
                        name: holiday.name,
                        startTime: holiday.date,
                        allDay: true,
                        color: colors.text
                    });
                }
            });

            const newBlackoutMap = {};
            const newBlackoutCounts = {};
            blackoutDates?.forEach(blackout => {
                const startDate = new Date(blackout.startDate);
                const endDate = new Date(blackout.endDate);

                const startMidnight = new Date(startDate);
                startMidnight.setHours(0, 0, 0, 0);
                const endMidnight = new Date(endDate);
                endMidnight.setHours(0, 0, 0, 0);

                const cursor = new Date(startMidnight);
                while (cursor <= endMidnight) {
                    const dateString = cursor.toDateString();
                    const isFirstDay = cursor.toDateString() === startMidnight.toDateString();
                    const isLastDay = cursor.toDateString() === endMidnight.toDateString();
                    const startHour = startDate.getHours();
                    const endHour = endDate.getHours();
                    const endMin = endDate.getMinutes();

                    const startsAtMidnight = isFirstDay && startHour === 0;
                    const endsAtEndOfDay = isLastDay && (endHour > 22 || (endHour === 23 && endMin >= 59));
                    const isMiddleDay = !isFirstDay && !isLastDay;

                    const isFull = isMiddleDay || (startsAtMidnight && endsAtEndOfDay) ||
                        (isFirstDay && !isLastDay && startsAtMidnight) ||
                        (isLastDay && !isFirstDay && endsAtEndOfDay);

                    if (isFull || newBlackoutMap[dateString] === 'full') {
                        newBlackoutMap[dateString] = 'full';
                    } else {
                        newBlackoutMap[dateString] = 'partial';
                    }
                    newBlackoutCounts[dateString] = (newBlackoutCounts[dateString] || 0) + 1;
                    cursor.setDate(cursor.getDate() + 1);
                }
            });
            setBlackoutDatesMap(newBlackoutMap);
            setBlackoutCountsMap(newBlackoutCounts);

            setMarkedDates(colorsWithDates);
            setEventsByDate(eventsWithDates);
            changeSelectedDay(selectedDate);
        }
    }, [eventsAreLoading, monthShown, yearShown, filteredCalendars, selectedLocations, eventSearchText]);

    const handleSheetChange = useCallback((index) => {
        if (index == -1) {
            setSearchText("");
        }
    }, []);

    const filterEventsToDay = (i, selected_day) => {
        if (!i.endTime) {
            return new Date(i.startTime).toDateString() == selected_day.toDateString();
        }
        const floatingDate = new Date(i.startTime);
        floatingDate.setHours(0, 0, 0, 0);
        const endDate = new Date(i.endTime);
        endDate.setHours(23, 59, 59, 999);
        const dates = [];
        do {
            dates.push(floatingDate.toDateString());
            floatingDate.setDate(floatingDate.getDate() + 1);
        } while (floatingDate.getTime() <= endDate.getTime());
        return dates.includes(selected_day.toDateString());
    };

    const changeSelectedDay = (selected_day) => {
        const hd = new Holidays('US');
        const isHoliday = hd.isHoliday(selected_day);
        const dayInstances = updateEventListRef.current?.filter(i => filterEventsToDay(i, selected_day)) || [];

        if (isHoliday) {
            isHoliday?.forEach(h => {
                dayInstances.push({
                    allDay: true,
                    isHoliday: true,
                    startTime: h.date,
                    Event: { name: h.name }
                });
            });
        }
        setSelectedDate(selected_day);
        setDayInstances(dayInstances);
    };

    const refreshList = () => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.calendars.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.locations.all() });
        queryClient.invalidateQueries({ queryKey: QueryKeys.blackoutDates.all() });
    };

    const filterSheet = (item) => {
        return searchText == "" || item.name?.indexOf(searchText) > -1 || (item.description?.indexOf(searchText) > -1) || (item.detailedArea?.indexOf(searchText) > -1);
    };

    const getFilteredCalendars = async () => {
        const calendars = await AsyncStorage.getItem('@filteredCalendars');
        const parsed = calendars != null ? JSON.parse(calendars) : [];
        setFilteredCalendars(parsed);
    };

    const updateFilteredCalendars = async (id) => {
        const newFilteredCalendars = [...filteredCalendars];
        newFilteredCalendars.includes(id)
            ? newFilteredCalendars.splice(newFilteredCalendars.indexOf(id), 1)
            : newFilteredCalendars.push(id);
        setFilteredCalendars(newFilteredCalendars ? newFilteredCalendars : []);
        const jsonValue = JSON.stringify(newFilteredCalendars);
        await AsyncStorage.setItem('@filteredCalendars', jsonValue);
    };

    const renderEventItem = useCallback(({ item, index }) => {
        const instance = item;
        const previousDate = index - 1 >= 0 ? new Date(dayInstances[index - 1].startTime) : null;
        const showDateHeader = index === 0 || (previousDate && previousDate.toDateString() !== new Date(instance.startTime).toDateString());

        return (
            <View key={"event_" + instance.id} style={styles.eventItemContainer}>
                {showDateHeader && (
                    <View style={styles.dateHeader}>
                        <Text style={styles.dateHeaderText}>
                            {new Date(instance.startTime).toLocaleString('en-US', { weekday: 'long', month: 'long', day: "numeric" })}
                        </Text>
                    </View>
                )}
                <EventInstanceCard instance={instance} variant={'alternate'} />
            </View>
        );
    }, [dayInstances]);

    const blackoutsForThisDayCount = blackoutCountsMap[selectedDate.toDateString()] || 0;
    const blackoutsForThisDay = blackoutDates?.filter(b => {
        const sel = new Date(selectedDate); sel.setHours(0,0,0,0);
        const s = new Date(b.startDate); s.setHours(0,0,0,0);
        const e = new Date(b.endDate); e.setHours(0,0,0,0);
        return sel >= s && sel <= e;
    }) || [];

    const calendarColumn = (
        <View style={styles.calendarColumn(isLandscape, isPortrait)}>
            {/* Filters */}
            {!isPortrait && (
                <View style={styles.filtersContainer}>
                    <GuideTarget id="events.filters" style={styles.filtersLeft} scrollRef={guideScrollAdapter}>
                        {isLandscape && isIos ? (
                            <Pressable onPress={() => setCalendarSheetOpen(true)} style={styles.filterButton(filteredCalendars.length > 0)}>
                                <Unicon name="calendar-outline" size={16} colorVariant={filteredCalendars.length > 0 ? "primary" : "text"} />
                                <Text style={styles.filterButtonText(filteredCalendars.length > 0)}>
                                    {filteredCalendars.length > 0
                                        ? `${allCalendars?.length - filteredCalendars.length} of ${allCalendars?.length} calendars`
                                        : 'All Calendars'}
                                </Text>
                                <Unicon name="chevron-down-outline" size={14} colorVariant={filteredCalendars.length > 0 ? "primary" : "textSecondary"} />
                            </Pressable>
                        ) : (
                            <DropdownMenu
                                title="Filter by Calendar"
                                multiple
                                triggerComponent={
                                    <View style={styles.filterButton(filteredCalendars.length > 0)}>
                                        <Unicon name="calendar-outline" size={16} colorVariant={filteredCalendars.length > 0 ? "primary" : "text"} />
                                        <Text style={styles.filterButtonText(filteredCalendars.length > 0)}>
                                            {filteredCalendars.length > 0
                                                ? `${allCalendars?.length - filteredCalendars.length} of ${allCalendars?.length} calendars`
                                                : 'All Calendars'}
                                        </Text>
                                        <Unicon name="chevron-down-outline" size={14} colorVariant={filteredCalendars.length > 0 ? "primary" : "textSecondary"} />
                                    </View>
                                }
                                options={[
                                    { name: "Select All", onSelect: () => { setFilteredCalendars([]); AsyncStorage.setItem('@filteredCalendars', JSON.stringify([])); } },
                                    { name: "Select None", onSelect: () => { const allIds = allCalendars?.map(c => c.id) || []; setFilteredCalendars(allIds); AsyncStorage.setItem('@filteredCalendars', JSON.stringify(allIds)); } },
                                    { id: "calendar-separator", separator: true },
                                    ...(allCalendars?.map(c => ({
                                        name: c.name,
                                        selected: !filteredCalendars.includes(c.id),
                                        onSelect: () => updateFilteredCalendars(c.id)
                                    })) || [])
                                ]}
                            />
                        )}
                        <DropdownMenu
                                title="Filter by Location"
                                multiple
                                triggerComponent={
                                    <View style={styles.filterButton(selectedLocations.length > 0)}>
                                        <Unicon name="location-outline" size={16} colorVariant={selectedLocations.length > 0 ? "primary" : "text"} />
                                        <Text style={styles.filterButtonText(selectedLocations.length > 0)}>
                                            {selectedLocations.length > 0
                                                ? `${selectedLocations.length} location${selectedLocations.length > 1 ? 's' : ''}`
                                                : 'All Locations'}
                                        </Text>
                                        <Unicon name="chevron-down-outline" size={14} colorVariant={selectedLocations.length > 0 ? "primary" : "textSecondary"} />
                                    </View>
                                }
                                options={[
                                    { name: selectedLocations.length > 0 ? "Show All" : "✓ Showing All", onSelect: () => setSelectedLocations([]) },
                                    { name: selectedLocations.length < allLocations?.length ? "Select None" : "✓ Selected None", onSelect: () => { const allIds = allLocations?.map(l => l.id) || []; setSelectedLocations(allIds); } },
                                    { id: "location-separator", separator: true },
                                    ...(allLocations?.map(l => ({
                                        name: l.name,
                                        selected: selectedLocations.includes(l.id),
                                        onSelect: () => {
                                            setSelectedLocations(prev =>
                                                prev.includes(l.id)
                                                    ? prev.filter(id => id !== l.id)
                                                    : [...prev, l.id]
                                            );
                                        }
                                    })) || [])
                                ]}
                            />
                    </GuideTarget>
                    <GuideTarget id="events.search" style={styles.searchContainer} scrollRef={guideScrollAdapter}>
                        <SearchBox
                            searchText={eventSearchText}
                            onChangeText={setEventSearchText}
                            placeholder="Search events..."
                            textChangeDelay={300}
                        />
                    </GuideTarget>
                </View>
            )}

            <GuideTarget
                id="events.calendar"
                scrollRef={guideScrollAdapter}
                style={{ flex: isLandscape ? 1 : undefined, width: '100%' }}
                onLayout={(e) => {
                    const h = e.nativeEvent.layout.height;
                    if (h > 0 && Math.abs(h - calendarWrapHeight) > 1) {
                        setCalendarWrapHeight(h);
                    }
                }}
            >
                <Calendar
                    eventsAreLoading={eventsAreLoading}
                    markedDates={markedDates}
                    eventsByDate={eventsByDate}
                    collapsed={false}
                    onDayPress={changeSelectedDay}
                    onMonthChange={(new_month) => setMonthShown(new_month)}
                    onYearChange={(new_year) => setYearShown(new_year)}
                    onAddOccurrence={(date) => { setAddOccurrenceDate(date); setPickerOpen(true); }}
                    dayBoxHeightOverride={calendarDayBoxHeight}
                />
            </GuideTarget>
        </View>
    );

    const dayEventsHeader = (
        <GuideTarget id="events.day" style={styles.selectedDayHeader} scrollRef={guideScrollAdapter}>
            <View>
                <Text style={styles.selectedDayTitle}>
                    {selectedDate.toLocaleDateString('en-US', { weekday: 'long' })}
                </Text>
                <Text style={styles.selectedDayDate}>
                    {selectedDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                </Text>
            </View>
            <View style={styles.selectedDayRight}>
                {!isWeb && (
                    <View style={styles.dayMenu}>
                        {loggedInUser?.can?.("Create Events") && (
                            <>
                                <Pressable
                                    testID="day-menu-add-occurrence"
                                    style={styles.dayMenuButton}
                                    onPress={() => { setAddOccurrenceDate(selectedDate); setPickerOpen(true); }}
                                >
                                    <Unicon name="calendar-blank-multiple" library="material" size={16} colorVariant="events" />
                                    {isWeb && <Text style={styles.dayMenuButtonText}>Add Occurrence</Text>}
                                </Pressable>
                                <Pressable
                                    testID="day-menu-new-event"
                                    style={styles.dayMenuButton}
                                    onPress={() => router.push({ pathname: "/events/create", params: { defaultStartTime: selectedDate.toISOString() } })}
                                >
                                    <Unicon name="add-outline" size={16} colorVariant="events" />
                                    {isWeb && <Text style={styles.dayMenuButtonText}>New Event</Text>}
                                </Pressable>
                            </>
                        )}
                        <Pressable
                            testID="day-menu-add-blackout"
                            style={styles.dayMenuButton}
                            onPress={() => router.push({ pathname: "/events/blackouts/create" })}
                        >
                            <Unicon name="ban-outline" size={16} colorVariant="events" />
                            {isWeb && <Text style={styles.dayMenuButtonText}>Add Blackout</Text>}
                        </Pressable>
                    </View>
                )}
                {blackoutsForThisDayCount > 0 && (
                    <Pressable style={styles.blackoutBadge} onPress={() => setBlackoutSheetOpen(true)}>
                        <Unicon name="ban-outline" size={14} colorVariant="events" />
                        <Text style={styles.blackoutBadgeText}>{blackoutsForThisDayCount} Blackout{blackoutsForThisDayCount > 1 ? 's' : ''}</Text>
                    </Pressable>
                )}
            </View>
        </GuideTarget>
    );

    const dayEventsBody = eventsAreLoading ? (
        <View style={styles.loadingContainer}>
            <ActivityIndicator color={colors.primary} size="large" />
        </View>
    ) : dayInstances?.length === 0 ? (
        <View style={styles.emptyDayContainer}>
            <Unicon name="calendar-outline" size={48} colorVariant="textSecondary" />
            <Text style={styles.emptyDayText}>No events scheduled</Text>
            {loggedInUser?.can?.(["Create Events"]) && (
                <SolidButton
                    size="sm"
                    text="Create Event"
                    onPress={() => router.push({ pathname: "/events/create", params: { defaultStartTime: selectedDate.toISOString() } })}
                />
            )}
        </View>
    ) : null;

    // Portrait rows carry the card's surface so the item block reads as the
    // continuation of the card that starts in the list header. The last row
    // owns the bottom corners only when the spacer footer isn't filling the
    // card's min-height.
    const renderPortraitEventItem = useCallback(({ item, index }) => (
        <View style={[styles.portraitCardItem, index === dayInstances.length - 1 && portraitCardSpacer <= 0 && styles.portraitCardItemBottom]}>
            {renderEventItem({ item, index })}
        </View>
    ), [dayInstances, portraitCardSpacer, renderEventItem]);

    // The day-events card keeps its minHeight (300, matching styles.eventsColumn)
    // even though items now render outside the card's header View — the spacer
    // footer fills whatever the items don't.
    const handlePortraitContentSize = useCallback((_w, h) => {
        setPortraitCardSpacer(prev => {
            const itemsHeight = h - portraitHeaderHeight - prev;
            const next = Math.max(0, 300 - portraitCardTopHeight - itemsHeight);
            return Math.abs(next - prev) > 1 ? next : prev;
        });
    }, [portraitHeaderHeight, portraitCardTopHeight]);

    const portraitListHeader = (
        <View onLayout={(e) => setPortraitHeaderHeight(e.nativeEvent.layout.height)}>
            <View style={[styles.mainContent(isLandscape), { paddingBottom: 0 }]}>
                {calendarColumn}
                <View style={styles.rightColumn(isLandscape, isPortrait)}>
                    {dayEventsBody ? (
                        <View style={styles.eventsColumn(isLandscape, isPortrait)}>
                            {dayEventsHeader}
                            {dayEventsBody}
                        </View>
                    ) : (
                        <View style={styles.eventsColumnTop} onLayout={(e) => setPortraitCardTopHeight(e.nativeEvent.layout.height)}>
                            {dayEventsHeader}
                        </View>
                    )}
                </View>
            </View>
        </View>
    );

    const portraitCardCap = !eventsAreLoading && dayInstances?.length > 0
        ? <View style={styles.portraitCardCap(portraitCardSpacer)} />
        : null;

    return (
        <View style={styles.container}>
            {isLandscape ? (
                <ScrollView
                    ref={guideScrollRef}
                    refreshControl={<RefreshControl refreshing={false} onRefresh={refreshList}/>}
                    style={styles.scrollView}
                    contentInsetAdjustmentBehavior="automatic"
                    keyboardShouldPersistTaps="handled"
                    contentContainerStyle={styles.scrollContent}
                    showsVerticalScrollIndicator={false}
                >
                    {/* Main Content Area */}
                    <View style={styles.mainContent(isLandscape)}>
                        {/* Left Column - Calendar */}
                        {calendarColumn}

                        {/* Right Column - Selected Day Events + Coming Up */}
                        <View style={styles.rightColumn(isLandscape, isPortrait)}>
                            <View style={styles.eventsColumn(isLandscape, isPortrait)}>
                                {dayEventsHeader}

                                {dayEventsBody ?? (
                                    <View style={styles.eventsList}>
                                        <LegendList
                                            recycleItems
                                            keyExtractor={(item) => item.id}
                                            showsVerticalScrollIndicator={false}
                                            estimatedItemSize={80}
                                            data={dayInstances}
                                            renderItem={renderEventItem}
                                            keyboardShouldPersistTaps="handled"
                                        />
                                    </View>
                                )}
                            </View>
                        </View>
                    </View>
                </ScrollView>
            ) : (
                // Portrait: the LegendList owns scrolling so day cards stay
                // virtualized — the calendar and card header move into
                // ListHeaderComponent and each row carries the card's surface.
                <LegendList
                    ref={guideScrollRef}
                    refreshControl={<RefreshControl refreshing={false} onRefresh={refreshList}/>}
                    style={styles.scrollView}
                    contentInsetAdjustmentBehavior="automatic"
                    keyboardShouldPersistTaps="handled"
                    contentContainerStyle={[styles.scrollContent, { paddingBottom: 16 }]}
                    showsVerticalScrollIndicator={false}
                    recycleItems
                    keyExtractor={(item) => item.id}
                    estimatedItemSize={80}
                    data={eventsAreLoading ? [] : dayInstances}
                    renderItem={renderPortraitEventItem}
                    onContentSizeChange={handlePortraitContentSize}
                    ListHeaderComponent={portraitListHeader}
                    ListFooterComponent={portraitCardCap}
                />
            )}

            {/* Sheets */}
            <SheetModal title="Manage Calendars" isOpen={calendarSheetOpen} onCancel={() => setCalendarSheetOpen(false)} onChange={handleSheetChange}>
                <View style={styles.sheetContent}>
                    <View style={styles.sheetHeader}>
                        <View style={styles.sheetSearchRow}>
                            <View style={styles.sheetSearchContainer}>
                                <SearchBox searchText={searchText} onChangeText={(e) => setSearchText(e)} textChangeDelay={0} placeholder="Search calendars..." />
                            </View>
                            {loggedInUser?.can?.("Create Calendars") && (
                                <SolidButton 
                                    size="sm" 
                                    iconName="add" 
                                    iconBefore
                                    testID="NewCalendarButton"
                                    text="New" 
                                    onPress={() => {
                                        router.push({ pathname: "/events/calendars/create" });
                                        setCalendarSheetOpen(false);
                                    }} 
                                />
                            )}
                        </View>
                        <View style={styles.sheetSubtitleRow}>
                            <Text style={styles.sheetSubtitle}>
                                {allCalendars?.length || 0} calendar{allCalendars?.length !== 1 ? 's' : ''} • Tap to toggle visibility
                            </Text>
                            <View style={styles.sheetActions}>
                                <Pressable onPress={() => { setFilteredCalendars([]); AsyncStorage.setItem('@filteredCalendars', JSON.stringify([])); }} style={styles.sheetActionText}>
                                    <Text style={styles.sheetActionLink}>Show All</Text>
                                </Pressable>
                                <Text style={styles.sheetActionSeparator}>•</Text>
                                <Pressable onPress={() => { const allIds = allCalendars?.map(c => c.id) || []; setFilteredCalendars(allIds); AsyncStorage.setItem('@filteredCalendars', JSON.stringify(allIds)); }} style={styles.sheetActionText}>
                                    <Text style={styles.sheetActionLink}>Select None</Text>
                                </Pressable>
                            </View>
                        </View>
                    </View>
                    <ScrollView contentContainerStyle={{ paddingBottom:isLandscape ? 0 : 200}} showsVerticalScrollIndicator={false}keyboardShouldPersistTaps="handled"
>
                        <View style={styles.sheetListContainer}>
                            {allCalendars?.filter(filterSheet).map(c => {
                                const isVisible = !filteredCalendars.includes(c.id);
                                return (
                                    <View 
                                        key={c.id} 
                                        style={styles.sheetListItem(isVisible)}
                                    >
                                        <Pressable onPress={() => updateFilteredCalendars(c.id)} style={styles.sheetListItemLeft}>
                                            <View style={styles.sheetCalendarDot(c.color, isVisible)} />
                                            <View style={styles.sheetListItemInfo}>
                                                <View style={styles.sheetListItemTitleRow}>
                                                    <Text style={styles.sheetListItemTitle(isVisible)} numberOfLines={1}>{c.name}</Text>
                                                    {c.calendarType === "SMALL_GROUPS" && (
                                                        <Unicon name='people-outline' size={14} colorVariant="textSecondary" style={{ marginLeft: 4 }} />
                                                    )}
                                                </View>
                                                {c.description && <Text style={styles.sheetListItemSubtitle} numberOfLines={1}>{c.description}</Text>}
                                            </View>
                                        </Pressable>
                                        <View style={styles.sheetListItemRight}>
                                            <Pressable onPress={() => updateFilteredCalendars(c.id)}>
                                                <Unicon name={isVisible ? "eye-outline" : "eye-off-outline"} size={18} colorVariant={isVisible ? "primary" : "textSecondary"} />
                                            </Pressable>
                                            <DropdownMenu
                                                testID={c.name}
                                                options={[
                                                    { name: `Subscribe in ${isIos ? "Apple" : "Google"} Calendar`, onSelect: () => Linking.openURL(`${process.env.EXPO_PUBLIC_DB_URL}/storage/v1/object/public/${process.env.EXPO_PUBLIC_STORAGE_BUCKET_PUBLIC}/calendars/${loggedInUser?.organizationID}/${c.id}.ics`) },
                                                    { name: "Share Calendar Link", onSelect: async () => {
                                                        await Share.share({
                                                            message: isIos ? "Subscribe to the Reach calendar!" : `${process.env.EXPO_PUBLIC_DB_URL}/storage/v1/object/public/${process.env.EXPO_PUBLIC_STORAGE_BUCKET_PUBLIC}/calendars/${loggedInUser?.organizationID}/${c.id}.ics`,
                                                            url: `${process.env.EXPO_PUBLIC_DB_URL}/storage/v1/object/public/${process.env.EXPO_PUBLIC_STORAGE_BUCKET_PUBLIC}/calendars/${loggedInUser?.organizationID}/${c.id}.ics`
                                                        });
                                                    }},
                                                    ...(loggedInUser?.can?.("Edit Calendars") || loggedInUser?.id === c.createdByID ? [{ name: "Edit Calendar", onSelect: () => { setCalendarSheetOpen(false); router.push({ pathname: "/events/calendars/create", params: { id: c.id } }); }}] : []),
                                                    ...(loggedInUser?.can?.("Delete Calendars") ? [{ name: "Delete Calendar", destructive: true, onSelect: () => showAlert({ title: "Delete Calendar?", subtitle: "This will also remove all events associated with it.", isDestructive: true, onConfirm: () => deleteCalendarMutation.mutate(c) })}] : [])
                                                ]}
                                            /> 
                                        </View>
                                    </View>
                                );
                            })}
                        </View>
                    </ScrollView>
                </View>
            </SheetModal>

            <SheetModal title={`Blackouts for ${new Date(selectedDate).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`} isOpen={blackoutSheetOpen} onCancel={() => setBlackoutSheetOpen(false)} onChange={handleSheetChange}>
                <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ flex:1, paddingBottom: 20 }}keyboardShouldPersistTaps="handled"
>
                    {blackoutsForThisDay.map((b, i) => <BlackoutPeriodItem blackoutPeriod={b} key={i} linkToProfile />)}
                </ScrollView>
            </SheetModal>

            <LocationScheduleModal
                visible={scheduleModalOpen}
                onClose={() => setScheduleModalOpen(false)}
                date={selectedDate}
            />

            {pickerOpen && (
                <AddOccurrencePicker
                    open={pickerOpen}
                    date={addOccurrenceDate}
                    onClose={() => { setPickerOpen(false); setAddOccurrenceDate(null); }}
                />
            )}
        </View>
    );
};

const styles = StyleSheet.create(({ colors, fonts, shadows }, rt) => ({
    container: {
        flex: 1
    },
    scrollView: {
        height: '100%',
    },
    scrollContent: {
        height: rt.isLandscape ? '100%' : 'auto'
    },
    quickActionsContainer: {
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        backgroundColor: colors.surface
    },
    quickActionsScroll: {
        paddingHorizontal: 16,
        gap: 12
    },
    quickActionButton: (_variant) => ({
        alignItems: 'center',
        gap: 6,
        minWidth: 70
    }),
    quickActionIconContainer: (variant) => ({
        width: 48,
        height: 48,
        borderRadius: 24,
        backgroundColor: variant === 'primary' ? colors.primary : colors.hover,
        alignItems: 'center',
        justifyContent: 'center'
    }),
    quickActionLabel: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        textAlign: 'center'
    },
    filtersContainer: {
        width: '100%',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent:'space-between',
        marginBottom: 8,
        flexWrap: 'wrap'
    },
    filtersLeft: {
        width:'50%',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap'
    },
    searchContainer: {
        maxWidth: 300,
        flex: 1,
        minWidth: 150,
        alignSelf:'flex-end'
    },
    filterButton: (isActive) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 8,
        backgroundColor: isActive ? colors.primary + '15' : colors.surface,
        borderWidth: 1,
        borderColor: isActive ? colors.primary : colors.border
    }),
    filterButtonText: (isActive) => ({
        fontSize: 13,
        fontFamily: fonts.body,
        color: isActive ? colors.primary : colors.text
    }),
    mainContent: (isLandscape) => ({
        flexDirection: isLandscape ? 'row' : 'column',
        padding: 16,
        gap: isLandscape ? 12 : 16,
        alignItems: 'flex-start',
        height:isLandscape ? '100%' : 'auto'
    }),
    calendarColumn: (isLandscape, isPortrait) => ({
        flex: isLandscape ? 1 : undefined,
        width: isPortrait ? '100%' : 'auto',
        alignItems: isPortrait ? 'center' : 'flex-start',
        gap: 12,
        maxWidth: isLandscape ? 'none' : '100%',
        height: isLandscape ? '100%' : 'auto',
    }),
    rightColumn: (isLandscape) => ({
        width: isLandscape ? 320 : '100%',
        minWidth: isLandscape ? 280 : undefined,
        maxWidth: isLandscape ? 380 : '100%',
        gap: 12,
        height:isLandscape ? '100%' : 'auto',
        flex:1
    }),
    eventsColumn: (isLandscape, isPortrait) => ({
        flex: 1,
        width: '100%',
        height:isLandscape ? '100%' : 'auto',
        minHeight: isPortrait ? 300 : 350,
        alignItems: 'stretch',
        backgroundColor: colors.surface,
        borderRadius: 12,
        boxShadow: shadows.card,
        overflow: 'hidden'
    }),
    // Portrait: the events card is split across the LegendList header (top
    // segment), item rows, and a bottom cap — together they reproduce
    // eventsColumn's surface, radius, and shadow.
    eventsColumnTop: {
        flex: 1,
        width: '100%',
        alignItems: 'stretch',
        backgroundColor: colors.surface,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        boxShadow: shadows.card,
        overflow: 'hidden'
    },
    portraitCardItem: {
        marginHorizontal: 16,
        paddingHorizontal: 16,
        backgroundColor: colors.surface,
        boxShadow: shadows.card
    },
    portraitCardItemBottom: {
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        overflow: 'hidden'
    },
    portraitCardCap: (height) => ({
        marginHorizontal: 16,
        height,
        backgroundColor: colors.surface,
        borderBottomLeftRadius: 12,
        borderBottomRightRadius: 12,
        overflow: 'hidden',
        boxShadow: shadows.card
    }),
    legendContainer: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 16,
        borderWidth: 1,
        borderColor: colors.border,
        width: '100%',
        maxWidth: 500
    },
    legendTitle: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
        marginBottom: 12
    },
    legendList: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8
    },
    legendItem: (isFiltered) => ({
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 16,
        backgroundColor: isFiltered ? colors.background : colors.hover,
        gap: 6
    }),
    legendDot: (color, isFiltered) => ({
        width: 10,
        height: 10,
        borderRadius: 5,
        backgroundColor: color,
        opacity: isFiltered ? 0.4 : 1
    }),
    legendText: (isFiltered) => ({
        fontSize: 12,
        fontFamily: fonts.body,
        color: isFiltered ? colors.textSecondary : colors.text,
        maxWidth: 100
    }),
    legendMore: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.primary,
        paddingHorizontal: 10,
        paddingVertical: 6
    },
    selectedDayHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: 16,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    selectedDayRight: {
        alignItems: 'flex-end',
        gap: 6,
    },
    dayMenu: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        flexWrap: 'wrap',
    },
    dayMenuButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        backgroundColor: colors.events + '12',
    },
    dayMenuButtonText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.events,
    },
    selectedDayTitle: {
        fontSize: 18,
        fontFamily: fonts.heading,
        color: colors.text
    },
    selectedDayDate: {
        fontSize: 14,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginTop: 2
    },
    blackoutBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.events + '15',
        paddingHorizontal: 10,
        paddingVertical: 6,
        borderRadius: 16,
        gap: 4
    },
    blackoutBadgeText: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.events
    },
    loadingContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60
    },
    emptyDayContainer: {
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
        gap: 12
    },
    emptyDayText: {
        fontSize: 16,
        fontFamily: fonts.body,
        color: colors.textSecondary
    },
    emptyDayButton: {
        marginTop: 8,
        paddingHorizontal: 20,
        paddingVertical: 10,
        backgroundColor: colors.primary,
        borderRadius: 8
    },
    emptyDayButtonText: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.onPrimary
    },
    eventsList: {
        paddingHorizontal: 16,
        height: '100%',
        flex: 1
    },
    eventItemContainer: {
        marginBottom: 0
    },
    dateHeader: {
        paddingVertical: 8
    },
    dateHeaderText: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.textSecondary,
        textTransform: 'uppercase'
    },
    upcomingSection: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 16,
        borderWidth: 1,
        borderColor: colors.border,
        width: '100%',
        maxWidth: 500
    },
    upcomingSectionMobile: {
        paddingHorizontal: 16,
        paddingTop: 8
    },
    sectionTitle: {
        fontSize: 16,
        fontFamily: fonts.heading,
        color: colors.text,
        marginBottom: 12
    },
    upcomingList: {
        gap: 8
    },
    upcomingScrollMobile: {
        gap: 12,
        paddingRight: 16
    },
    upcomingEventCard: {
        flexDirection: 'row',
        backgroundColor: colors.background,
        borderRadius: 10,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: colors.border,
        minWidth: 180
    },
    upcomingEventColorBar: (color) => ({
        width: 4,
        backgroundColor: color || colors.primary
    }),
    upcomingEventContent: {
        flex: 1,
        padding: 12
    },
    upcomingEventDay: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.primary,
        textTransform: 'uppercase',
        marginBottom: 2
    },
    upcomingEventName: {
        fontSize: 14,
        fontFamily: fonts.heading,
        color: colors.text,
        marginBottom: 2
    },
    upcomingEventTime: {
        fontSize: 12,
        fontFamily: fonts.body,
        color: colors.textSecondary
    },
    sheetContent: {
       
    },
    sheetHeader: {
        
    },
    sheetSearchRow: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'center'
    },
    sheetSearchContainer: {
        flex: 1
    },
    sheetCreateButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.primary,
        paddingHorizontal: 14,
        paddingVertical: 10,
        borderRadius: 8,
        gap: 4
    },
    sheetCreateButtonText: {
        color: colors.onPrimary,
        fontFamily: fonts.bold,
        fontSize: 14
    },
    sheetSubtitle: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginTop: 12
    },
    sheetSubtitleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    sheetActions: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8
    },
    sheetActionText: {
        paddingVertical: 2
    },
    sheetActionLink: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.primary
    },
    sheetActionSeparator: {
        fontSize: 13,
        color: colors.textSecondary
    },
    sheetListContainer: {
        gap: 8,
    },
    sheetListItem: (isActive) => ({
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: isActive ? colors.surface : colors.background,
        borderRadius: 10,
        padding: 12,
        borderWidth: 1,
        borderColor: isActive ? colors.border : colors.border + '80',
        opacity: isActive ? 1 : 0.7
    }),
    sheetListItemLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
        flex: 1
    },
    sheetCalendarDot: (color, isActive) => ({
        width: 16,
        height: 16,
        borderRadius: 8,
        backgroundColor: color ?? colors.primary,
        opacity: isActive ? 1 : 0.5
    }),
    sheetLocationIcon: {
        width: 36,
        height: 36,
        alignItems: 'center',
        justifyContent: 'center'
    },
    sheetListItemInfo: {
        flex: 1
    },
    sheetListItemTitleRow: {
        flexDirection: 'row',
        alignItems: 'center'
    },
    sheetListItemTitle: (isActive) => ({
        fontSize: 15,
        fontFamily: fonts.heading,
        color: isActive ? colors.text : colors.textSecondary
    }),
    sheetListItemSubtitle: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textSecondary,
        marginTop: 2
    },
    sheetListItemRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8
    }
}));

export default CalendarScreen;
